import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import {
  Lock as LockIcon,
} from '@mui/icons-material';
import { loadStripe, PaymentRequest } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import { apiService } from '../services/api';

interface PaymentDetails {
  paymentId: string;
  amount: number;
  currency: string;
  description: string;
  status: string;
  expiresAt?: string | null; // Add expiresAt to interface
  vendor: {
    businessName: string;
    logo?: string;
    stripePublishableKey?: string;
  };
  customer?: {
    name?: string;
    email?: string;
  };
  order?: {
    orderId: string;
    items: Array<{
      name: string;
      description?: string;
      quantity: number;
      price: number;
      total: number;
    }>;
    subtotal: number;
    tax: number;
    platformFee: number;
    total: number;
  };
  items?: Array<{
    name: string;
    description?: string;
    quantity: number;
    price: number;
    total: number;
  }>;
  subtotal: number;
  tax: number;
  platformFee: number;
  total: number;
}

function PaymentForm({ paymentDetails }: { paymentDetails: PaymentDetails }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [email, setEmail] = useState(paymentDetails.customer?.email || '');

  useEffect(() => {
    if (!stripe) {
      console.warn('[PaymentPage] Stripe not loaded yet');
      return;
    }

    // Check if we're on HTTPS (required for Payment Request API)
    const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    if (!isSecure) {
      console.warn('[PaymentPage] Payment Request API requires HTTPS');
    }

    // Build display items from order items
    const items = paymentDetails.order?.items || paymentDetails.items || [];
    const displayItems = items.map(item => ({
      label: `${item.quantity}x ${item.name}`,
      amount: Math.round(item.total * 100), // Convert to cents
    }));

    // If no display items, use a single item with the total
    const finalDisplayItems = displayItems.length > 0 
      ? displayItems 
      : [{
          label: paymentDetails.description || 'Payment',
          amount: Math.round(paymentDetails.total * 100),
        }];

    const totalAmount = Math.round(paymentDetails.total * 100);
    
    // Validate amount is greater than 0
    if (totalAmount <= 0) {
      console.error('[PaymentPage] Invalid payment amount:', totalAmount);
      return;
    }

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: paymentDetails.currency.toLowerCase(),
      total: {
        label: paymentDetails.vendor.businessName,
        amount: totalAmount,
      },
      displayItems: finalDisplayItems,
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isChrome = /Chrome/.test(userAgent) && !/Safari/.test(userAgent.replace(/Chrome.*/, ''));
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      
      console.log('[PaymentPage] Payment Request availability:', {
        canMakePayment: !!result,
        applePay: result?.applePay,
        googlePay: result?.googlePay,
        hasStripe: !!stripe,
        total: paymentDetails.total,
        totalAmount,
        currency: paymentDetails.currency,
        isSecure,
        userAgent,
        isIOS,
        isChrome,
        isSafari,
        publishableKey: paymentDetails.vendor?.stripePublishableKey ? 'present' : 'missing',
      });
      
      if (result) {
        console.log('[PaymentPage] ✅ Payment Request available, enabling Apple Pay/Google Pay');
        setPaymentRequest(pr);
      } else {
        let reason = 'Payment Request API not available';
        if (isIOS && isChrome) {
          reason = 'Apple Pay is only available in Safari on iOS, not in Chrome';
        } else if (!isSecure) {
          reason = 'Payment Request API requires HTTPS';
        } else if (totalAmount <= 0) {
          reason = 'Invalid payment amount';
        }
        console.warn(`[PaymentPage] ⚠️ ${reason}`);
      }
    }).catch((error) => {
      console.error('[PaymentPage] ❌ Error checking Payment Request availability:', error);
    });

    pr.on('paymentmethod', async (ev) => {
      setProcessing(true);
      setError(null);

      try {
        // In a real implementation, you would create a payment intent here
        // For now, we'll simulate a successful payment
        setTimeout(() => {
          ev.complete('success');
          setProcessing(false);
          navigate(`/payment-success/${paymentDetails.paymentId}`);
        }, 1000);
      } catch (err: any) {
        ev.complete('fail');
        setError(err.message || 'Payment failed');
        setProcessing(false);
      }
    });
  }, [stripe, paymentDetails, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setError('Card element not found');
      setProcessing(false);
      return;
    }

    try {
      // In a real implementation, you would create a payment intent and confirm it
      // For now, we'll simulate a successful payment
      setTimeout(() => {
        setProcessing(false);
        navigate(`/payment-success/${paymentDetails.paymentId}`);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  const items = paymentDetails.order?.items || paymentDetails.items || [];

  return (
    <form onSubmit={handleSubmit}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <List>
            {items.map((item, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemText
                  primary={item.name}
                  secondary={
                    <>
                      {item.quantity > 1 && `${item.quantity} × `}
                      ${item.price.toFixed(2)}
                    </>
                  }
                />
                <Typography variant="body1" fontWeight="bold">
                  ${item.total.toFixed(2)}
                </Typography>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>Subtotal</Typography>
            <Typography>${paymentDetails.subtotal.toFixed(2)}</Typography>
          </Box>
          {paymentDetails.tax > 0 && (
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>Tax</Typography>
              <Typography>${paymentDetails.tax.toFixed(2)}</Typography>
            </Box>
          )}
          {paymentDetails.platformFee > 0 && (
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary" variant="body2">
                Processing Fee
              </Typography>
              <Typography color="text.secondary" variant="body2">
                ${paymentDetails.platformFee.toFixed(2)}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6" fontWeight="bold">
              Total
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              ${paymentDetails.total.toFixed(2)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {paymentRequest ? (
        <Box sx={{ mb: 3 }}>
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  theme: 'dark',
                  height: '48px',
                },
              },
            }}
          />
        </Box>
      ) : (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {/iPad|iPhone|iPod/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent) && !/Safari/.test(navigator.userAgent.replace(/Chrome.*/, '')) 
              ? '💡 Tip: Apple Pay is only available in Safari on iOS. Please open this page in Safari to use Apple Pay.'
              : 'Apple Pay and Google Pay are not available in this browser. Please use the card form below.'}
          </Typography>
        </Box>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Information
          </Typography>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 3 }}
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Card Details
            </Typography>
            <Box
              sx={{
                p: 2,
                border: '1px solid rgba(255, 255, 255, 0.23)',
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }}
            >
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#fff',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#fa755a',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={!stripe || processing}
        startIcon={processing ? <CircularProgress size={20} /> : <LockIcon />}
        sx={{
          py: 1.5,
          fontSize: '1.1rem',
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5568d3 0%, #653a8f 100%)',
          },
        }}
      >
        {processing ? 'Processing...' : `Pay $${paymentDetails.total.toFixed(2)}`}
      </Button>
    </form>
  );
}

export default function PaymentPage() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get('vendorId');
  const token = searchParams.get('token'); // Extract token from URL
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!paymentId || !vendorId) {
        setError('Payment ID and Vendor ID are required');
        setLoading(false);
        return;
      }

      if (!token) {
        setError('Payment token is required for security');
        setLoading(false);
        return;
      }

      try {
        const details = await apiService.getPaymentDetails(paymentId, vendorId, token);
        
        console.log('[PaymentPage] Received payment details:', details);
        console.log('[PaymentPage] Details type:', typeof details);
        console.log('[PaymentPage] Details keys:', details ? Object.keys(details) : 'null/undefined');
        
        if (!details) {
          console.error('[PaymentPage] Details is null/undefined');
          setError('Payment details not found');
          setLoading(false);
          return;
        }
        
        // Defensive check: ensure details is an object
        if (typeof details !== 'object' || details === null || Array.isArray(details)) {
          console.error('[PaymentPage] Invalid payment details format:', typeof details, details);
          setError('Invalid payment details format');
          setLoading(false);
          return;
        }
        
        // Check if required fields exist
        if (!details.paymentId || !details.vendor) {
          console.error('[PaymentPage] Missing required fields:', {
            hasPaymentId: !!details.paymentId,
            hasVendor: !!details.vendor,
            detailsKeys: Object.keys(details),
          });
          setError('Invalid payment details: missing required fields');
          setLoading(false);
          return;
        }
        
        setPaymentDetails(details);
        // Safely access expiresAt - check if it exists and is a string
        let expiresAtValue: string | null = null;
        try {
          if (details && typeof details === 'object' && 'expiresAt' in details) {
            expiresAtValue = details.expiresAt || null;
          }
          console.log('[PaymentPage] Setting expiresAt:', expiresAtValue, 'from details:', details ? 'exists' : 'null');
        } catch (err) {
          console.error('[PaymentPage] Error accessing expiresAt:', err);
          expiresAtValue = null;
        }
        setExpiresAt(expiresAtValue);
        
        // Initialize Stripe with vendor's publishable key
        const publishableKey = details.vendor?.stripePublishableKey || 
                              process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
                              'pk_test_51ScvXEHlSGnIcOVeRL3cfe3R7ziVfsQylaPzHJz35o5qmpPjX3XNdbDhk0HLKg4MfoLXZ75mUzDrjtxfn36WiEOw00O1hlkdkS';
        
        if (publishableKey) {
          setStripePromise(loadStripe(publishableKey));
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [paymentId, vendorId, token]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !paymentDetails) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="error">{error || 'Payment not found'}</Alert>
        </Container>
      </Box>
    );
  }

  // If payment is completed, show receipt instead of payment form
  if (paymentDetails.status === 'completed') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Card
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box textAlign="center" mb={4}>
                {paymentDetails.vendor?.logo && (
                  <Box
                    component="img"
                    src={paymentDetails.vendor.logo}
                    alt={paymentDetails.vendor.businessName}
                    sx={{ maxHeight: 60, mb: 2 }}
                  />
                )}
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {paymentDetails.vendor?.businessName || 'Payment Receipt'}
                </Typography>
                <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                  Payment completed successfully!
                </Alert>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" gutterBottom>
                Order Details
              </Typography>
              {(paymentDetails.order?.items || paymentDetails.items)?.map((item, index) => (
                <Box key={index} display="flex" justifyContent="space-between" mb={2}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {item.quantity}x {item.name}
                    </Typography>
                    {item.description && (
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body1">
                    ${item.total.toFixed(2)}
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">
                  ${(paymentDetails.order?.subtotal || paymentDetails.subtotal).toFixed(2)}
                </Typography>
              </Box>
              {(paymentDetails.order?.tax || paymentDetails.tax) > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body1">Tax</Typography>
                  <Typography variant="body1">
                    ${(paymentDetails.order?.tax || paymentDetails.tax).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {(paymentDetails.order?.platformFee || paymentDetails.platformFee) > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body1">Processing Fee</Typography>
                  <Typography variant="body1">
                    ${(paymentDetails.order?.platformFee || paymentDetails.platformFee).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  ${(paymentDetails.order?.total || paymentDetails.total).toFixed(2)}
                </Typography>
              </Box>

              <Box mt={4} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Payment ID: {paymentDetails.paymentId}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <LockIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Your payment was securely processed.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // If payment is not pending and not completed, show error
  if (paymentDetails.status !== 'pending') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="info">
            This payment has already been processed.
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!paymentDetails) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="error">
            {error || 'Payment details not found'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Card
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={4}>
              {paymentDetails.vendor?.logo && (
                <Box
                  component="img"
                  src={paymentDetails.vendor.logo}
                  alt={paymentDetails.vendor.businessName}
                  sx={{ maxHeight: 60, mb: 2 }}
                />
              )}
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {paymentDetails.vendor?.businessName || 'Payment'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {paymentDetails.description || 'Complete your payment'}
              </Typography>
            </Box>

            {/* Expiration Warning */}
            {expiresAt && typeof expiresAt === 'string' && (() => {
              try {
                const expiration = new Date(expiresAt);
                if (isNaN(expiration.getTime())) {
                  return null; // Invalid date
                }
                const now = new Date();
                const diffMs = expiration.getTime() - now.getTime();
                const isExpired = diffMs <= 0;
                const isExpiringSoon = diffMs < 3600000; // Less than 1 hour
                
                if (isExpired) {
                  return (
                    <Alert severity="error" sx={{ mb: 3 }} icon={<LockIcon />}>
                      This payment link has expired. Please request a new payment link.
                    </Alert>
                  );
                }
                
                const hours = Math.floor(diffMs / (1000 * 60 * 60));
                const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                
                return (
                  <Alert 
                    severity={isExpiringSoon ? 'warning' : 'info'} 
                    sx={{ mb: 3 }}
                    icon={<LockIcon />}
                  >
                    {isExpiringSoon 
                      ? <>This payment link expires in <strong>{timeStr}</strong>. Please complete your payment soon.</>
                      : <>This payment link expires in <strong>{timeStr}</strong>.</>}
                  </Alert>
                );
              } catch (err) {
                console.error('Error processing expiration date:', err);
                return null;
              }
            })()}

            <Divider sx={{ my: 4 }} />

            {paymentDetails && stripePromise ? (
              <Elements stripe={stripePromise}>
                <PaymentForm paymentDetails={paymentDetails} />
              </Elements>
            ) : paymentDetails ? (
              <Alert severity="warning">
                Stripe is not configured. Please contact the business to complete payment.
              </Alert>
            ) : null}

            <Box mt={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                <LockIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                Secure payment powered by Stripe
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

